import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { NavigationItem } from '@/types';
import { useShellI18n } from '@/features/language/useI18n';
import { getNavIcon } from '@/features/navigation/icons';
import styles from './NavItem.module.css';

interface NavItemProps {
  item: NavigationItem;
  isCollapsed: boolean;
  depth?: number;
}

export const NavItem: React.FC<NavItemProps> = ({ item, isCollapsed, depth = 0 }) => {
  const location = useLocation();
  const { t } = useShellI18n();
  const [expanded, setExpanded] = React.useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const itemLabel = t(item.label);

  React.useEffect(() => {
    if (hasChildren && !isCollapsed) {
      const isChildActive = item.children?.some(
        (child) =>
          location.pathname === child.path ||
          (child.path && location.pathname.startsWith(child.path + '/'))
      );
      if (isChildActive) {
        setExpanded(true);
      }
    }
  }, [location.pathname, hasChildren, isCollapsed, item.children]);

  const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (!hasChildren) {
      return;
    }
    event.preventDefault();
    if (!isCollapsed) {
      setExpanded((current) => !current);
    }
  };

  const Icon = React.useMemo<React.ComponentType | undefined>(() => {
    if (typeof item.icon === 'string') {
      return getNavIcon(item.icon);
    }
    return item.icon as React.ComponentType | undefined;
  }, [item.icon]);

  const content = (
    <>
      <div className={styles.iconWrapper}>{Icon && <Icon />}</div>
      {!isCollapsed && (
        <div className={styles.labelWrapper}>
          <span className={styles.label}>{itemLabel}</span>
          {item.badge && <span className={styles.badge}>{item.badge}</span>}
          {hasChildren && (
            <span className={`${styles.arrow} ${expanded ? styles.expanded : ''}`}>
              <ChevronDown aria-hidden="true" />
            </span>
          )}
        </div>
      )}
    </>
  );

  return (
    <li className={styles.itemWrapper}>
      {item.path && !hasChildren ? (
        <NavLink
          to={item.path}
          className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
          style={{ paddingLeft: `${depth * 1.5 + 1}rem` }}
          title={isCollapsed ? itemLabel : undefined}
        >
          {content}
        </NavLink>
      ) : (
        <div
          className={`${styles.link} ${hasChildren ? styles.parent : ''}`}
          onClick={handleClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleClick(event);
            }
          }}
          style={{ paddingLeft: `${depth * 1.5 + 1}rem` }}
          title={isCollapsed ? itemLabel : undefined}
          role="button"
          tabIndex={0}
        >
          {content}
        </div>
      )}

      {hasChildren && !isCollapsed && expanded && (
        <ul className={styles.subList}>
          {item.children?.map((child) => (
            <NavItem key={child.id} item={child} isCollapsed={isCollapsed} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
};

export default NavItem;
